import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { DataStore } from 'aws-amplify';
import { captureError } from '../common/sentry';
import { toQmListRoleOrNull } from '../common/qmRoles';
import { useDatastore } from '../hooks/useDatastore';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { UserDetailsProps } from '../datastore/userDetails';
import { UserProfile } from '../models';

interface UserDetailsContextType {
  userDetails: UserDetailsProps | undefined;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

const UserDetailsContext = createContext<UserDetailsContextType | undefined>(undefined);

export function UserDetailsProvider({ children }: { children: React.ReactNode }) {
  const store = useDatastore();
  const user = useCurrentUser();
  const cognitoID = user.cognitoUser?.username;

  const [userDetails, setUserDetails] = useState<UserDetailsProps | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchUserDetails = useCallback(async () => {
    // Guard: wait until everything is ready
    if (!store.isDatastoreReady || store.isSyncing || !store.userDetails || !cognitoID) {
      return;
    }

    try {
      setIsLoading(true);

      const details = await store.userDetails.get(cognitoID, user.cognitoUser);

      setUserDetails(details);
    } catch (error) {
      console.error('Error fetching user details:', error);
      captureError(error, 'UserDetailsContext-fetchUserDetails');
    } finally {
      setIsLoading(false);
    }
  }, [cognitoID, store.isDatastoreReady, store.isSyncing, store.userDetails, user.cognitoUser]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  useEffect(() => {
    if (!cognitoID) return;

    const subscription = DataStore.observe(UserProfile).subscribe({
      next: ({ opType, element }) => {
        if (opType === 'DELETE' || element.cognitoID !== cognitoID) return;

        setUserDetails((previous) => {
          if (!previous) return previous;

          return {
            ...previous,
            email: element.email ?? null,
            firstname: element.firstname ?? null,
            lastname: element.lastname ?? null,
            adminPanelAccess: element.adminPanelAccess ?? null,
            qmListRole: toQmListRoleOrNull(element.qmListRole)
          };
        });
      },
      error: (error) => {
        console.error('UserDetails live profile subscription failed:', error);
        captureError(error, 'UserDetailsContext-live-profile');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [cognitoID]);

  const refetch = async () => {
    await fetchUserDetails();
  };

  return (
    <UserDetailsContext.Provider value={{ userDetails, isLoading, refetch }}>{children}</UserDetailsContext.Provider>
  );
}

export function useUserDetails() {
  const context = useContext(UserDetailsContext);

  if (!context) {
    throw new Error('useUserDetails must be used within a UserDetailsProvider');
  }

  return context;
}
